import React from "react";
import {
  Box,
  Button,
  Modal,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
} from "@mui/material";
import ExcelIcon from "../../assets/excel-icon.svg";

const UploadTemplateModal = ({
  open,
  onClose,
  excelData,
  selectedFile,
  onFileChange,
  onChangeFile,
  onDragOver,
  onDrop,
  onSave,
  uploading,
}) => {
  return (
    <Modal
      open={open}
      onClose={onClose}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: "rgba(255, 255, 255, 0.1)",
            backdropFilter: "blur(5px)",
          },
        },
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: excelData ? "90%" : 500,
          maxWidth: excelData ? "1400px" : "500px",
          bgcolor: "background.paper",
          boxShadow: 24,
          borderRadius: 2,
          px: 3,
          py: 2,
          overflow: "auto",
        }}
      >
        <div
          style={{
            display: "flex",
            gap: "1.5rem",
            alignItems: "center",
          }}
        >
          <img
            src={ExcelIcon}
            alt="Excel Icon"
            style={{ width: "20px", height: "20px" }}
          />

          <div>
            <h2 style={{ color: "var(--pink-contentoh)" }}>
              Vista previa de validación
            </h2>
          </div>
        </div>

        {!excelData && (
          <Box
            onDragOver={onDragOver}
            onDrop={onDrop}
            sx={{
              border: "2px dashed var(--pink-contentoh)",
              borderRadius: "12px",
              padding: "24px",
              textAlign: "center",
              backgroundColor: "#f9f9f9",
              transition: "all 0.3s ease",
              cursor: "pointer",
              marginTop: 3,
            }}
          >
            <input
              accept=".xlsx"
              style={{ display: "none" }}
              id="xlsx-file-input"
              type="file"
              onChange={onFileChange}
            />
            {!selectedFile && (
              <Typography
                sx={{
                  mb: 2,
                  color: "#666",
                  fontSize: "16px",
                  fontWeight: 500,
                }}
              >
                Arrastra y suelta tu archivo aquí
              </Typography>
            )}
            <label htmlFor="xlsx-file-input">
              <Button
                variant="outlined"
                component="span"
                sx={{
                  borderColor: "var(--pink-contentoh)",
                  color: "var(--pink-contentoh)",
                  "&:hover": {
                    borderColor: "var(--pink-contentoh)",
                    backgroundColor: "rgba(255, 105, 180, 0.04)",
                  },
                }}
              >
                {selectedFile ? "Cambiar archivo" : "Seleccionar archivo Excel"}
              </Button>
            </label>
            {selectedFile && (
              <Typography sx={{ mt: 2, color: "#666", fontSize: "14px" }}>
                Archivo seleccionado: <strong>{selectedFile.name}</strong>
              </Typography>
            )}
            {!selectedFile && (
              <Typography sx={{ mt: 2, color: "#999", fontSize: "14px" }}>
                Formatos aceptados: .xlsx
              </Typography>
            )}
          </Box>
        )}

        {excelData &&
          (() => {
            // Obtener todos los nombres únicos de atributos y descripciones
            const allColumns = new Set();
            excelData.articles.forEach((article) => {
              article.attributes.forEach((attr) => allColumns.add(attr.name));
              article.descriptions.forEach((desc) => allColumns.add(desc.name));
            });
            const columns = Array.from(allColumns);

            return (
              <Box sx={{ mt: 3, mb: 2 }}>
                <TableContainer component={Paper} sx={{ maxHeight: "70vh" }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        {columns.map((columnName, idx) => (
                          <TableCell
                            key={idx}
                            sx={{
                              width: "auto",
                              whiteSpace: "nowrap",
                              textTransform: "uppercase",
                              backgroundColor: "#D637A4",
                              color: "#FFF",
                              border: "0.5px solid #fff",
                              position: "sticky",
                              top: 0,
                              left: 0,
                              zIndex: idx === 0 ? 2 : 1,
                            }}
                          >
                            {columnName}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {excelData.articles.map((article, rowIndex) => {
                        // Crear un mapa de nombre -> valor para este artículo
                        const valueMap = {};
                        article.attributes.forEach((attr) => {
                          valueMap[attr.name] = attr.value;
                        });
                        article.descriptions.forEach((desc) => {
                          valueMap[desc.name] = desc.value;
                        });

                        return (
                          <TableRow
                            key={rowIndex}
                            sx={{ "&:hover": { bgcolor: "#f9f9f9" } }}
                          >
                            {columns.map((columnName, colIndex) => (
                              <TableCell
                                key={colIndex}
                                sx={{
                                  textWrap: "nowrap",
                                  border: "0.5px solid rgba(0, 0, 0, 0.1)",
                                  position: colIndex === 0 ? "sticky" : "unset",
                                  left: 0,
                                  zIndex: colIndex === 0 ? 1 : 0,
                                  backgroundColor:
                                    colIndex === 0
                                      ? "#FFF"
                                      : columnName?.value
                                      ? columnName.value
                                      : columnName?.obligatory === "Obligatorio"
                                      ? "red"
                                      : "unset",
                                }}
                              >
                                {valueMap[columnName] || "-"}
                              </TableCell>
                            ))}
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            );
          })()}

        <Box
          sx={{
            mt: 3,
            display: "flex",
            gap: 2,
            justifyContent: "space-between",
          }}
        >
          <Box>
            {excelData && (
              <Button
                variant="outlined"
                onClick={onChangeFile}
                disabled={uploading}
                sx={{
                  borderRadius: "8px",
                  textTransform: "none",
                  borderColor: "var(--pink-contentoh)",
                  color: "var(--pink-contentoh)",
                  "&:hover": {
                    borderColor: "var(--pink-contentoh)",
                    backgroundColor: "rgba(255, 105, 180, 0.04)",
                  },
                }}
              >
                Cambiar
              </Button>
            )}
          </Box>
          <Box sx={{ display: "flex", gap: 2 }}>
            <Button
              variant="outlined"
              onClick={onClose}
              disabled={uploading}
              sx={{
                borderRadius: "8px",
                textTransform: "none",
                borderColor: "var(--purple)",
                color: "var(--purple)",
                "&:hover": {
                  borderColor: "var(--purple)",
                  backgroundColor: "var(--purple-light-contentoh)",
                  color: "#ffffff",
                },
              }}
            >
              Cancelar
            </Button>
            {excelData && (
              <Button
                variant="contained"
                onClick={onSave}
                disabled={uploading}
                sx={{
                  borderRadius: "8px",
                  textTransform: "none",
                  borderColor: "var(--pink-contentoh)",
                  color: "var(--white-contentoh)",
                  backgroundColor: "var(--pink-contentoh)",
                  boxShadow: "none",
                  "&:hover": {
                    borderColor: "var(--pink-contentoh)",
                    backgroundColor: "var(--pink-contentoh)",
                    color: "#ffffff",
                    boxShadow: "none",
                  },
                }}
              >
                {uploading ? (
                  <>
                    <CircularProgress
                      size={20}
                      sx={{ color: "white", mr: 1 }}
                    />
                    Subiendo...
                  </>
                ) : (
                  "Guardar"
                )}
              </Button>
            )}
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default UploadTemplateModal;
